/**
 * StatusBadge Component
 * Visual badges for document and section statuses
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { 
  FileEdit, 
  Clock, 
  Send, 
  Eye, 
  CheckCircle2, 
  XCircle,
  Circle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { DOCUMENT_STATUS, SECTION_STATUS, USER_STATUS } from '@/constants/beStudyMockData';

/**
 * Document status configuration
 */
const DOCUMENT_STATUS_CONFIG = {
  [DOCUMENT_STATUS.DRAFT]: {
    label: 'Draft',
    icon: FileEdit,
    variant: 'secondary',
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700',
  },
  [DOCUMENT_STATUS.IN_PROGRESS]: {
    label: 'In Progress',
    icon: Loader2,
    variant: 'default',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    iconClassName: 'animate-spin',
  },
  [DOCUMENT_STATUS.PENDING_REVIEW]: {
    label: 'Pending Review',
    icon: Clock,
    variant: 'warning',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  },
  [DOCUMENT_STATUS.IN_REVIEW]: {
    label: 'In Review',
    icon: Eye,
    variant: 'default',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800',
  },
  [DOCUMENT_STATUS.APPROVED]: {
    label: 'Approved',
    icon: CheckCircle2,
    variant: 'success',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800',
  },
  [DOCUMENT_STATUS.REJECTED]: {
    label: 'Rejected',
    icon: XCircle,
    variant: 'destructive',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
  },
};

/**
 * Section status configuration
 */
const SECTION_STATUS_CONFIG = {
  [SECTION_STATUS.NOT_STARTED]: {
    label: 'Not Started',
    icon: Circle,
    variant: 'outline',
    className: 'bg-slate-50 text-slate-500 dark:bg-slate-900 dark:text-slate-400 border-slate-200 dark:border-slate-700',
  },
  [SECTION_STATUS.IN_PROGRESS]: {
    label: 'In Progress',
    icon: Loader2,
    variant: 'default',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    iconClassName: 'animate-spin',
  },
  [SECTION_STATUS.COMPLETED]: {
    label: 'Completed',
    icon: CheckCircle2,
    variant: 'success',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800',
  },
  [SECTION_STATUS.NEEDS_REVISION]: {
    label: 'Needs Revision',
    icon: AlertCircle,
    variant: 'warning',
    className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800',
  },
};

/**
 * User status configuration
 */
const USER_STATUS_CONFIG = {
  [USER_STATUS.ACTIVE]: {
    label: 'Active',
    icon: CheckCircle2,
    variant: 'success',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800',
  },
  [USER_STATUS.INACTIVE]: {
    label: 'Inactive',
    icon: XCircle,
    variant: 'secondary',
    className: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700',
  },
};

/**
 * Generic status badge component
 */
export function StatusBadge({ 
  status, 
  type = 'document', 
  size = 'default',
  showIcon = true,
  showLabel = true,
  className 
}) {
  // Get appropriate config based on type
  let config;
  switch (type) {
    case 'section':
      config = SECTION_STATUS_CONFIG[status];
      break;
    case 'user':
      config = USER_STATUS_CONFIG[status];
      break;
    case 'document':
    default:
      config = DOCUMENT_STATUS_CONFIG[status];
  }

  // Fallback for unknown status
  if (!config) {
    config = {
      label: status || 'Unknown',
      icon: Circle,
      variant: 'outline',
      className: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
    };
  }

  const Icon = config.icon;
  
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5 gap-1',
    default: 'text-xs px-2 py-1 gap-1.5',
    lg: 'text-sm px-2.5 py-1.5 gap-2',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    default: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium border',
        sizeClasses[size],
        config.className,
        className
      )}
      data-testid={`status-badge-${type}`}
      data-status={status}
    >
      {showIcon && Icon && (
        <Icon className={cn(iconSizes[size], config.iconClassName)} />
      )}
      {showLabel && <span>{config.label}</span>}
    </span>
  );
}

/**
 * Document-specific status badge
 */
export function DocumentStatusBadge({ status, size = 'default', showIcon = true, className }) {
  return (
    <StatusBadge 
      status={status} 
      type="document" 
      size={size} 
      showIcon={showIcon} 
      className={className}
    />
  );
}

/**
 * Section-specific status badge
 */
export function SectionStatusBadge({ status, size = 'default', showIcon = true, className }) {
  return (
    <StatusBadge 
      status={status} 
      type="section" 
      size={size} 
      showIcon={showIcon} 
      className={className}
    />
  );
}

/**
 * User-specific status badge
 */
export function UserStatusBadge({ status, size = 'default', showIcon = true, className }) {
  return (
    <StatusBadge 
      status={status} 
      type="user" 
      size={size} 
      showIcon={showIcon} 
      className={className}
    />
  );
}

/**
 * Role badge
 */
export function RoleBadge({ role, size = 'default', className }) {
  const roleConfig = {
    ADMIN: { label: 'Admin', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800' },
    USER: { label: 'User', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800' },
    REVIEWER: { label: 'Reviewer', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800' },
  };

  const config = roleConfig[role] || { label: role, className: 'bg-slate-100 text-slate-500' };

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    default: 'text-xs px-2 py-1',
    lg: 'text-sm px-2.5 py-1.5',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium border',
        sizeClasses[size],
        config.className,
        className
      )}
      data-testid="role-badge"
    >
      {config.label}
    </span>
  );
}

/**
 * Priority badge
 */
export function PriorityBadge({ priority, size = 'default', className }) {
  const priorityConfig = {
    high: { label: 'High', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200' },
    medium: { label: 'Medium', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200' },
    low: { label: 'Low', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200' },
  };

  const config = priorityConfig[priority?.toLowerCase()] || priorityConfig.medium;

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    default: 'text-xs px-2 py-1',
    lg: 'text-sm px-2.5 py-1.5',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium border',
        sizeClasses[size],
        config.className,
        className
      )}
      data-testid="priority-badge"
    >
      {config.label}
    </span>
  );
}

export default StatusBadge;
